import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';
import Swal from 'sweetalert2';
import type { EmpresaTenant } from './models';
import workflowService from './workflowService';

// ─── Types ────────────────────────────────────────────────────────────────────

interface CoberturaDashboard {
  tenantId: number;
  nomeEmpresa: string;
  totalContas: number;
  contasVinculadas: number;
  contasPendentes: number;
  percentualCobertura: number;
  statusCobertura: 'VERMELHO' | 'AMARELO' | 'VERDE';
  podeAtivarWorkflow: boolean;
  pendencias: PendenciaCadastro[];
}

interface PendenciaCadastro {
  contaId: number;
  tenantId: number;
  nomeEmpresa: string;
  grupoEconomico: string;
  codigo: string;
  codigoReduzido: string;
  descricao: string;
  grupoConta: string | null;
  nivel: number;
  tipo: string;
}

interface CoberturaGlobal {
  totalEmpresas: number;
  empresasVerde: number;
  empresasAmarelo: number;
  empresasVermelho: number;
  totalContasGlobal: number;
  contasVinculadasGlobal: number;
  percentualGlobal: number;
  goLiveBloqueado: boolean;
  empresas: CoberturaDashboard[];
}

type ModoVisualizacao = 'empresa' | 'global';

// ─── KPI ProgressBar (empresa única) ─────────────────────────────────────────

const CoberturaKPI: React.FC<{ dados: CoberturaDashboard }> = ({ dados }) => {
  const pct = dados.percentualCobertura;
  const cor = pct >= 100 ? '#50cd89' : pct >= 70 ? '#ffc700' : '#f64e60';
  const corBg = pct >= 100 ? '#f0fff4' : pct >= 70 ? '#fffbea' : '#fff1f2';
  const icone = pct >= 100 ? '✅' : pct >= 70 ? '⚠️' : '🚨';
  const texto = pct >= 100 ? 'Cobertura total — Go-Live liberado' : pct >= 70 ? 'Atenção — contas sem aprovador' : 'Crítico — cobertura insuficiente para Go-Live';

  return (
    <section className='wf-card' style={{ marginBottom: 20 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
        <div>
          <h2 style={{ margin: 0, fontSize: 17 }}>Cobertura de Aprovadores SOX</h2>
          <p style={{ margin: '4px 0 0', fontSize: 13, color: '#7e8299' }}>{dados.nomeEmpresa}</p>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ fontSize: 28, fontWeight: 800, color: cor }}>{pct.toFixed(1)}%</div>
          <div style={{ fontSize: 12, color: '#7e8299' }}>{dados.contasVinculadas}/{dados.totalContas} contas</div>
        </div>
      </div>

      <div style={{ height: 16, borderRadius: 8, background: '#f1f1f4', overflow: 'hidden', marginBottom: 12 }}>
        <div style={{
          height: '100%',
          width: `${Math.min(pct, 100)}%`,
          background: `linear-gradient(90deg, ${cor}cc, ${cor})`,
          borderRadius: 8,
          transition: 'width 0.6s ease',
        }} />
      </div>

      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <div style={{ display: 'inline-flex', alignItems: 'center', gap: 8, background: corBg, border: `1.5px solid ${cor}`, borderRadius: 20, padding: '6px 14px', fontSize: 13, fontWeight: 600, color: cor }}>
          {icone} {texto}
        </div>
        {dados.contasPendentes > 0 && (
          <span style={{ fontSize: 13, color: '#7e8299' }}>
            {dados.contasPendentes} conta(s) sem aprovador designado
          </span>
        )}
      </div>

      <div style={{ display: 'flex', gap: 12, marginTop: 16, flexWrap: 'wrap' }}>
        {[
          { label: 'Total de Contas', value: dados.totalContas, cor: '#009ef7' },
          { label: 'Vinculadas', value: dados.contasVinculadas, cor: '#50cd89' },
          { label: 'Pendentes', value: dados.contasPendentes, cor: dados.contasPendentes > 0 ? '#f64e60' : '#50cd89' },
        ].map(m => (
          <div key={m.label} style={{ flex: '1 1 100px', minWidth: 90, background: '#f8f9fa', borderRadius: 10, padding: '10px 14px', borderLeft: `4px solid ${m.cor}` }}>
            <div style={{ fontSize: 11, color: '#7e8299', marginBottom: 4 }}>{m.label}</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: m.cor }}>{m.value}</div>
          </div>
        ))}
      </div>
    </section>
  );
};

// ─── Painel Global (todos os tenants) ────────────────────────────────────────

const PainelGlobal: React.FC<{
  global: CoberturaGlobal;
  onVerEmpresa: (t: CoberturaDashboard) => void;
}> = ({ global, onVerEmpresa }) => {
  const pct = global.percentualGlobal;
  const cor = pct >= 100 ? '#50cd89' : pct >= 70 ? '#ffc700' : '#f64e60';

  return (
    <>
      {/* KPI Global */}
      <section className='wf-card' style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 12, marginBottom: 16 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 17 }}>Cobertura Global — Todas as Empresas</h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: '#7e8299' }}>{global.totalEmpresas} empresa(s) com plano ativo</p>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: 28, fontWeight: 800, color: cor }}>{pct.toFixed(1)}%</div>
            <div style={{ fontSize: 12, color: '#7e8299' }}>{global.contasVinculadasGlobal}/{global.totalContasGlobal} contas</div>
          </div>
        </div>

        <div style={{ height: 16, borderRadius: 8, background: '#f1f1f4', overflow: 'hidden', marginBottom: 16 }}>
          <div style={{
            height: '100%',
            width: `${Math.min(pct, 100)}%`,
            background: `linear-gradient(90deg, ${cor}cc, ${cor})`,
            borderRadius: 8,
            transition: 'width 0.6s ease',
          }} />
        </div>

        {/* Hard Stop Banner */}
        {global.goLiveBloqueado && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff1f2', border: '1.5px solid #f64e60', borderRadius: 10, padding: '10px 16px', marginBottom: 16 }}>
            <span style={{ fontSize: 20 }}>🚫</span>
            <div>
              <strong style={{ fontSize: 13, color: '#f64e60' }}>Go-Live Bloqueado — Cobertura incompleta</strong>
              <div style={{ fontSize: 12, color: '#7e8299', marginTop: 2 }}>
                {global.empresasVermelho + global.empresasAmarelo} empresa(s) com aprovadores pendentes. Vincule todos antes do Go-Live.
              </div>
            </div>
          </div>
        )}

        {/* Cards por status */}
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {[
            { label: 'Empresas', value: global.totalEmpresas, cor: '#009ef7' },
            { label: '✅ Cobertura 100%', value: global.empresasVerde, cor: '#50cd89' },
            { label: '⚠️ Cobertura parcial', value: global.empresasAmarelo, cor: '#ffc700' },
            { label: '🚨 Cobertura crítica', value: global.empresasVermelho, cor: '#f64e60' },
          ].map(m => (
            <div key={m.label} style={{ flex: '1 1 110px', minWidth: 100, background: '#f8f9fa', borderRadius: 10, padding: '10px 14px', borderLeft: `4px solid ${m.cor}` }}>
              <div style={{ fontSize: 11, color: '#7e8299', marginBottom: 4 }}>{m.label}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: m.cor }}>{m.value}</div>
            </div>
          ))}
        </div>
      </section>

      {/* Grid por empresa */}
      <section className='wf-card' style={{ marginBottom: 20 }}>
        <h3 style={{ margin: '0 0 14px', fontSize: 15 }}>Cobertura por Empresa</h3>
        <div style={{ overflowX: 'auto' }}>
          <table className='wf-table'>
            <thead>
              <tr>
                <th>Empresa</th>
                <th style={{ textAlign: 'center' }}>Total</th>
                <th style={{ textAlign: 'center' }}>Vinculadas</th>
                <th style={{ textAlign: 'center' }}>Pendentes</th>
                <th style={{ minWidth: 160 }}>Cobertura</th>
                <th style={{ textAlign: 'center' }}>Status</th>
                <th style={{ width: 100 }} />
              </tr>
            </thead>
            <tbody>
              {global.empresas.map(e => {
                const p = e.percentualCobertura;
                const c = p >= 100 ? '#50cd89' : p >= 70 ? '#ffc700' : '#f64e60';
                return (
                  <tr key={e.tenantId}>
                    <td style={{ fontWeight: 600, fontSize: 13 }}>{e.nomeEmpresa}</td>
                    <td style={{ textAlign: 'center', fontSize: 13 }}>{e.totalContas}</td>
                    <td style={{ textAlign: 'center', fontSize: 13, color: '#50cd89', fontWeight: 700 }}>{e.contasVinculadas}</td>
                    <td style={{ textAlign: 'center', fontSize: 13, color: e.contasPendentes > 0 ? '#f64e60' : '#50cd89', fontWeight: 700 }}>{e.contasPendentes}</td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, height: 8, borderRadius: 4, background: '#f1f1f4', overflow: 'hidden' }}>
                          <div style={{ height: '100%', width: `${Math.min(p, 100)}%`, background: c, borderRadius: 4, transition: 'width 0.4s ease' }} />
                        </div>
                        <span style={{ fontSize: 12, fontWeight: 700, color: c, minWidth: 42 }}>{p.toFixed(1)}%</span>
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`wf-badge wf-badge--${e.statusCobertura === 'VERDE' ? 'concluida' : e.statusCobertura === 'AMARELO' ? 'em_andamento' : 'rejeitada'}`}>
                        {e.statusCobertura === 'VERDE' ? '✅ OK' : e.statusCobertura === 'AMARELO' ? '⚠️ Parcial' : '🚨 Crítico'}
                      </span>
                    </td>
                    <td>
                      <button
                        className='wf-btn wf-btn--secondary wf-btn--sm'
                        onClick={() => onVerEmpresa(e)}
                        title='Ver detalhes desta empresa'
                      >
                        Ver detalhes
                      </button>
                    </td>
                  </tr>
                );
              })}
              {global.empresas.length === 0 && (
                <tr><td colSpan={7} style={{ textAlign: 'center', color: '#a1a5b7', padding: '32px 0' }}>Nenhuma empresa com plano ativo.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </>
  );
};

// ─── Grid de Pendências ───────────────────────────────────────────────────────

const GridPendencias: React.FC<{
  pendencias: PendenciaCadastro[];
  busca: string;
  onVincular: (p: PendenciaCadastro) => void;
}> = ({ pendencias, busca, onVincular }) => {
  const filtradas = useMemo(() => {
    if (!busca.trim()) return pendencias;
    const b = busca.toLowerCase();
    return pendencias.filter(p =>
      p.codigo.toLowerCase().includes(b)
      || p.descricao.toLowerCase().includes(b)
      || (p.grupoConta ?? '').toLowerCase().includes(b)
      || p.codigoReduzido.toLowerCase().includes(b)
    );
  }, [pendencias, busca]);

  const agrupadas = useMemo((): [string, PendenciaCadastro[]][] => {
    const map = new Map<string, PendenciaCadastro[]>();
    filtradas.forEach(p => {
      const g = p.grupoConta?.trim() || 'Sem grupo';
      if (!map.has(g)) map.set(g, []);
      map.get(g)!.push(p);
    });
    return Array.from(map.entries());
  }, [filtradas]);

  if (filtradas.length === 0) {
    return (
      <div style={{ textAlign: 'center', padding: '40px 24px', color: '#a1a5b7' }}>
        {pendencias.length === 0
          ? <><div style={{ fontSize: 40 }}>✅</div><p style={{ marginTop: 12 }}>Todas as contas têm aprovador designado!</p></>
          : <p>Nenhum resultado para "{busca}".</p>
        }
      </div>
    );
  }

  return (
    <div className='wf-table-wrap' style={{ overflowX: 'auto' }}>
      <table className='wf-table'>
        <thead>
          <tr>
            <th>Código</th>
            <th>Descrição</th>
            <th>Tipo</th>
            <th>Nível</th>
            <th>Status</th>
            <th style={{ width: 100 }} />
          </tr>
        </thead>
        <tbody>
          {agrupadas.map(([grupo, contas]) => (
            <React.Fragment key={grupo}>
              <tr style={{ background: '#f5f8ff', borderTop: '2px solid #dfe3ea' }}>
                <td colSpan={5} style={{ padding: '8px 10px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span style={{ fontSize: 12 }}>📁</span>
                    <strong style={{ fontSize: 13, color: '#3f4254' }}>{grupo}</strong>
                    <span className='wf-badge wf-badge--aguardando_aprovacao'>{contas.length} pendente(s)</span>
                  </div>
                </td>
                <td />
              </tr>
              {contas.map(p => (
                <tr key={p.contaId}>
                  <td>
                    <strong style={{ fontSize: 13 }}>{p.codigo}</strong>
                    {p.codigoReduzido && <span style={{ display: 'block', fontSize: 11, color: '#a1a5b7' }}>{p.codigoReduzido}</span>}
                  </td>
                  <td style={{ maxWidth: 280 }}>
                    <span style={{ fontSize: 13, paddingLeft: (p.nivel - 1) * 12 }}>{p.descricao}</span>
                  </td>
                  <td>
                    <span style={{ fontSize: 11, background: p.tipo === 'SINTETICA' ? '#f5f8ff' : '#f0fff4', color: p.tipo === 'SINTETICA' ? '#3f6ad8' : '#107c10', padding: '2px 8px', borderRadius: 12, fontWeight: 600 }}>
                      {p.tipo}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: '#7e8299' }}>N{p.nivel}</td>
                  <td><span className='wf-badge wf-badge--aguardando_aprovacao'>⚠ Pendente</span></td>
                  <td>
                    <button
                      className='wf-btn wf-btn--primary wf-btn--sm'
                      onClick={() => onVincular(p)}
                      title='Ir para Matriz e vincular este aprovador'
                    >
                      Vincular Agora
                    </button>
                  </td>
                </tr>
              ))}
            </React.Fragment>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ─── DashboardIntegridadePage ─────────────────────────────────────────────────

const DashboardIntegridadePage: React.FC = () => {
  const history = useHistory();

  const [modo, setModo] = useState<ModoVisualizacao>('empresa');
  const [empresas, setEmpresas] = useState<EmpresaTenant[]>([]);
  const [empresaSelecionada, setEmpresaSelecionada] = useState<number>(0);
  const [cobertura, setCobertura] = useState<CoberturaDashboard | null>(null);
  const [coberturaGlobal, setCoberturaGlobal] = useState<CoberturaGlobal | null>(null);
  const [carregando, setCarregando] = useState(false);
  const [busca, setBusca] = useState('');
  const [grupoFiltro, setGrupoFiltro] = useState('');
  const [notificando, setNotificando] = useState(false);
  const [exportando, setExportando] = useState(false);

  useEffect(() => {
    // Só empresas de fato configuradas no Workflow SOX (grupo vinculado + ativa) — mesmo
    // critério usado no seletor de empresa do restante do módulo.
    workflowService.listarTenants().then(lista => {
      setEmpresas(lista.filter(e => e.grupoId != null && e.status === 'ATIVA'));
    }).catch(() => {});
  }, []);

  const carregarGlobal = useCallback(() => {
    setCarregando(true);
    setCoberturaGlobal(null);
    workflowService.matrizCoberturaGlobal()
      .then(d => setCoberturaGlobal(d as CoberturaGlobal))
      .catch(() => setCoberturaGlobal(null))
      .finally(() => setCarregando(false));
  }, []);

  const carregarCobertura = useCallback((tenantId: number) => {
    if (!tenantId) return;
    setCarregando(true);
    setCobertura(null);
    setBusca('');
    setGrupoFiltro('');
    workflowService.matrizCobertura(tenantId)
      .then(d => setCobertura(d as CoberturaDashboard))
      .catch(() => setCobertura(null))
      .finally(() => setCarregando(false));
  }, []);

  const handleModoChange = (novoModo: ModoVisualizacao) => {
    setModo(novoModo);
    if (novoModo === 'global') carregarGlobal();
  };

  const handleEmpresaChange = (id: number) => {
    setEmpresaSelecionada(id);
    carregarCobertura(id);
  };

  const gruposDisponiveis = useMemo(() => {
    if (!cobertura) return [];
    const set = new Set(cobertura.pendencias.map(p => p.grupoConta?.trim() || 'Sem grupo'));
    return Array.from(set).sort();
  }, [cobertura]);

  // Seletor direto em vez de rolar a lista toda pra achar o grupo — útil quando há
  // centenas de contas pendentes como no caso acima (321).
  const pendenciasDoGrupo = useMemo(() => {
    if (!cobertura) return [];
    if (!grupoFiltro) return cobertura.pendencias;
    return cobertura.pendencias.filter(p => (p.grupoConta?.trim() || 'Sem grupo') === grupoFiltro);
  }, [cobertura, grupoFiltro]);

  const handleVincular = (p: PendenciaCadastro) => {
    history.push(`/workflow-aprovacao/matriz-responsabilidades?tenantId=${p.tenantId}&highlight=${p.contaId}`);
  };

  const handleVerEmpresaGlobal = (e: CoberturaDashboard) => {
    setModo('empresa');
    setEmpresaSelecionada(e.tenantId);
    setCobertura(e);
    setBusca('');
  };

  const handleExportar = async () => {
    if (!empresaSelecionada) return;
    setExportando(true);
    try {
      const blob = await workflowService.matrizExportarPendencias(empresaSelecionada);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `pendencias_matriz_${empresaSelecionada}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch {
      Swal.fire({ icon: 'error', title: 'Erro ao exportar', text: 'Não foi possível gerar o CSV.', confirmButtonText: 'OK' });
    } finally {
      setExportando(false);
    }
  };

  const handleNotificar = async () => {
    if (!empresaSelecionada || !cobertura) return;
    if (cobertura.podeAtivarWorkflow) {
      Swal.fire({ icon: 'info', title: 'Sem pendências', text: 'Cobertura 100% — nenhum alerta necessário.', timer: 3000, showConfirmButton: false, toast: true, position: 'top-end' });
      return;
    }
    const confirm = await Swal.fire({
      icon: 'question',
      title: 'Notificar Admins SOX?',
      html: `Será enviado um alerta a todos os administradores N7 sobre as <b>${cobertura.contasPendentes} contas pendentes</b> (${cobertura.percentualCobertura.toFixed(1)}% de cobertura).`,
      showCancelButton: true,
      confirmButtonText: 'Enviar notificação',
      cancelButtonText: 'Cancelar',
    });
    if (!confirm.isConfirmed) return;

    setNotificando(true);
    try {
      const resultado = await workflowService.matrizNotificarPendencias(empresaSelecionada);
      Swal.fire({
        icon: 'success',
        title: 'Notificação enviada!',
        text: `Alerta enviado para ${resultado.notificados ?? 'os'} administrador(es) SOX.`,
        timer: 4000,
        showConfirmButton: false,
        toast: true,
        position: 'top-end',
      });
    } catch {
      Swal.fire({ icon: 'error', title: 'Erro ao notificar', text: 'Tente novamente.', confirmButtonText: 'OK' });
    } finally {
      setNotificando(false);
    }
  };

  return (
    <div className='wf-page'>
      {/* Cabeçalho */}
      <div className='wf-header'>
        <div>
          <h1>Dashboard de Integridade de Cadastros</h1>
          <p>Relatório de cobertura da Matriz SOX — aprovadores × contas contábeis.</p>
        </div>
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {modo === 'empresa' && (
            <>
              <button
                className='wf-btn wf-btn--secondary'
                onClick={handleExportar}
                disabled={!cobertura || exportando}
              >
                {exportando ? 'Exportando...' : '⬇ Exportar CSV'}
              </button>
              <button
                className='wf-btn wf-btn--warning'
                onClick={handleNotificar}
                disabled={!cobertura || notificando || cobertura.podeAtivarWorkflow}
                title={cobertura?.podeAtivarWorkflow ? 'Sem pendências para notificar' : 'Enviar alerta semanal SOX para admins N7'}
              >
                {notificando ? 'Enviando...' : '🔔 Notificar Admin SOX'}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Toggle Empresa / Global */}
      <section className='wf-card' style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', background: '#f1f1f4', borderRadius: 8, padding: 3, gap: 3 }}>
            {(['empresa', 'global'] as ModoVisualizacao[]).map(m => (
              <button
                key={m}
                onClick={() => handleModoChange(m)}
                style={{
                  padding: '6px 18px', borderRadius: 6, border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  background: modo === m ? '#ffffff' : 'transparent',
                  color: modo === m ? '#181c32' : '#7e8299',
                  boxShadow: modo === m ? '0 1px 4px rgba(0,0,0,0.10)' : 'none',
                  transition: 'all 0.2s',
                }}
              >
                {m === 'empresa' ? '🏢 Empresa' : '🌐 Visão Global'}
              </button>
            ))}
          </div>
          {carregando && <span style={{ fontSize: 13, color: '#a1a5b7' }}>Carregando...</span>}
        </div>

        {/* Seletor de empresa (modo empresa) */}
        {modo === 'empresa' && (
          <label style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap', marginTop: 14 }}>
            <span style={{ fontWeight: 600, fontSize: 14, whiteSpace: 'nowrap' }}>Empresa:</span>
            <select
              className='wf-input'
              style={{ minWidth: 280, maxWidth: 420 }}
              value={empresaSelecionada}
              onChange={e => handleEmpresaChange(Number(e.target.value))}
            >
              <option value={0}>— Selecione uma empresa —</option>
              {empresas.map(e => (
                <option key={e.id} value={e.id}>{e.nome}</option>
              ))}
            </select>
          </label>
        )}
      </section>

      {/* ── Modo Empresa ── */}
      {modo === 'empresa' && (
        <>
          {!empresaSelecionada && (
            <section className='wf-card' style={{ textAlign: 'center', padding: '48px 24px', color: '#a1a5b7' }}>
              <div style={{ fontSize: 48, marginBottom: 12 }}>📊</div>
              <strong style={{ fontSize: 15, color: '#5e6278' }}>Selecione uma empresa para visualizar a cobertura</strong>
              <p style={{ margin: '8px 0 0', fontSize: 13 }}>O dashboard calculará em tempo real a porcentagem de contas com aprovadores designados.</p>
            </section>
          )}

          {cobertura && (
            <>
              <CoberturaKPI dados={cobertura} />

              {/* Hard Stop banner */}
              {!cobertura.podeAtivarWorkflow && cobertura.totalContas > 0 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 10, background: '#fff1f2', border: '1.5px solid #f64e60', borderRadius: 10, padding: '10px 16px', marginBottom: 16 }}>
                  <span style={{ fontSize: 20 }}>🚫</span>
                  <div>
                    <strong style={{ fontSize: 13, color: '#f64e60' }}>Hard Stop: Go-Live bloqueado</strong>
                    <div style={{ fontSize: 12, color: '#7e8299', marginTop: 2 }}>
                      Cobertura de {cobertura.percentualCobertura.toFixed(1)}% — vincule todos os aprovadores para liberar o Go-Live SOX.
                    </div>
                  </div>
                </div>
              )}

              {cobertura.contasPendentes > 0 && (
                <section className='wf-card' style={{ marginBottom: 16 }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                    <h3 style={{ margin: 0, fontSize: 15 }}>Contas Pendentes de Aprovador</h3>
                    <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                      <select
                        className='wf-input'
                        style={{ minWidth: 180 }}
                        value={grupoFiltro}
                        onChange={e => setGrupoFiltro(e.target.value)}
                      >
                        <option value=''>Todos os grupos</option>
                        {gruposDisponiveis.map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                      <input
                        className='wf-input'
                        style={{ minWidth: 220 }}
                        placeholder='Buscar conta...'
                        value={busca}
                        onChange={e => setBusca(e.target.value)}
                      />
                      <button
                        className='wf-btn wf-btn--secondary wf-btn--sm'
                        onClick={() => history.push(`/workflow-aprovacao/matriz-responsabilidades?tenantId=${empresaSelecionada}`)}
                        title='Abrir Matriz de Responsabilidades'
                      >
                        🗂️ Abrir Matriz
                      </button>
                    </div>
                  </div>

                  <GridPendencias
                    pendencias={pendenciasDoGrupo}
                    busca={busca}
                    onVincular={handleVincular}
                  />
                </section>
              )}

              {cobertura.podeAtivarWorkflow && (
                <section className='wf-card' style={{ textAlign: 'center', padding: '32px 24px', background: '#f0fff4', border: '1.5px solid #50cd89' }}>
                  <div style={{ fontSize: 40, marginBottom: 8 }}>✅</div>
                  <strong style={{ fontSize: 16, color: '#00a86b' }}>Cobertura 100% — Go-Live Liberado</strong>
                  <p style={{ margin: '8px 0 0', fontSize: 13, color: '#5e6278' }}>
                    Todas as {cobertura.totalContas} contas contábeis têm aprovadores designados com validação SoD.
                  </p>
                </section>
              )}
            </>
          )}
        </>
      )}

      {/* ── Modo Global ── */}
      {modo === 'global' && coberturaGlobal && (
        <PainelGlobal global={coberturaGlobal} onVerEmpresa={handleVerEmpresaGlobal} />
      )}

      {modo === 'global' && !coberturaGlobal && !carregando && (
        <section className='wf-card' style={{ textAlign: 'center', padding: '48px 24px', color: '#a1a5b7' }}>
          <div style={{ fontSize: 48, marginBottom: 12 }}>🌐</div>
          <p>Não foi possível carregar a visão global. Tente novamente.</p>
          <button className='wf-btn wf-btn--primary' onClick={carregarGlobal} style={{ marginTop: 12 }}>
            Tentar novamente
          </button>
        </section>
      )}
    </div>
  );
};

export default DashboardIntegridadePage;
